import {useConfig} from '@aboutyou/config';
import {SponsoredContentSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/sections.types';
import {
  BrandLogo,
  COLORS,
  CardImage,
  CardImageCTA,
  CardImageContent,
  FONT_SIZES,
  FONT_WEIGHTS,
  ImageRatio,
  ProductImage,
  Text,
  VideoOrFallbackImageWrapper,
  mediaMobileOnly,
  mobileOnlyLinaria,
  useInView,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {SponsoredPlacementHeader} from './SponsoredPlacementHeader';

interface SponsoredPlacementCardProps extends SponsoredContentSection {
  className?: string;
  style?: React.CSSProperties;
  hideFollowBrandLabel?: boolean;
  imageRatio: ImageRatio;
  onClick?: VoidFunction;
  promotionTracking: SponsoredContentSection['promotionTracking'];
  // this should be used on mWeb if the SponsoredPlacement is wrapped in a slider
  reduceOffscreenSize?: boolean;
  showCtaOnHover?: boolean;
  hasProducts?: boolean;
  isSlider?: boolean;
}

export const SponsoredPlacementCard: React.FC<SponsoredPlacementCardProps> = ({
  className,
  style,
  color,
  cta,
  headline,
  hideFollowBrandLabel,
  image,
  imageRatio,
  video,
  onClick,
  productImages,
  promotionTracking,
  reduceOffscreenSize,
  showCtaOnHover,
  sponsored,
  brand,
  targetUrl,
  hasProducts = false,
  isSlider = false,
}) => {
  const {ref, inView} = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '500px',
  });
  const {device} = useConfig();
  const shouldShowHeadline = device === 'desktop' || !brand;

  return (
    <Wrapper
      data-testid="sponsoredPlacementCard"
      data-is-slider={isSlider}
      className={className}
      style={style}
      data-reduced-size={reduceOffscreenSize}
      $hasProducts={hasProducts}
      ref={ref as React.RefObject<HTMLDivElement>}
    >
      <Header
        brand={brand}
        hideFollowBrandLabel={hideFollowBrandLabel}
        sponsored={sponsored}
        promotionTracking={promotionTracking}
      />
      <StyledCard
        data-testid="sponsoredPlacementCardImage"
        data-is-slider={isSlider}
        image={image}
        imageRatio={imageRatio}
        video={inView ? video : undefined}
        breakpoints={[]}
        cta={{label: cta?.label, url: targetUrl.url}}
        showCtaOnHover={showCtaOnHover}
        onClick={onClick}
        $vibrantColor={color.hex}
        imageLazy
      >
        {brand && (
          <StyledBrandLogo
            data-testid="sponsoredPlacementCardBrandLogo"
            name={brand.name}
            image={brand.deprecatedWhiteLogoImage}
            maxWidth="50%"
            showTooltip={false}
          />
        )}
        {shouldShowHeadline && (
          <Headline color="White" textAlignment="center">
            {headline}
          </Headline>
        )}
        {productImages.length > 0 && (
          <ProductList data-testid="sponsoredPlacementCardProductList">
            {productImages.map((image, index) => (
              <StyledProductImage
                key={index}
                src={image.url}
                alt={image.description}
                widths={[120, 240, 480]}
                breakpoints={[{columns: 4}]}
                padded
              />
            ))}
          </ProductList>
        )}
      </StyledCard>
    </Wrapper>
  );
};

const Wrapper = styled.div<{$hasProducts?: boolean}>`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 4px;
  transition: transform 0.3s ease;
  will-change: transform;
  transform: scale(1);
  aspect-ratio: ${props => (props.$hasProducts ? '1/1.2' : '1/1')};

  &[data-reduced-size='true'] {
    ${mobileOnlyLinaria(`transform: scale(0.97, 0.92);`)}
  }
`;

const StyledCard = styled(CardImage)<{
  $vibrantColor: string;
  showCtaOnHover?: boolean;
}>`
  width: 100%;
  height: 100%;

  background: ${props => props.$vibrantColor};

  ${CardImageContent} {
    gap: 16px;

    ${mobileOnlyLinaria(`padding: 24px`)}
  }

  ${VideoOrFallbackImageWrapper} {
    position: relative;
    height: max-content;

    img {
      object-fit: unset;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 30%;
      pointer-events: none;
      background: ${props =>
        `linear-gradient(
        0deg,
        ${props.$vibrantColor} 0%,
        ${props.$vibrantColor}00 100%
      )`};
    }
  }

  ${CardImageCTA} {
    width: fit-content;
    margin: 0 auto;
  }

  @media ${mediaMobileOnly} {
    width: calc(100% - 16px);
    background: transparent;

    ${VideoOrFallbackImageWrapper} {
      height: 50%;

      img {
        object-fit: cover;
      }

      &::after {
        display: none;
      }
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: ${props =>
        `linear-gradient(
        180deg,
        transparent 100%,
        rgba(0, 0, 0, 0.2) 28.5%
      ), linear-gradient(180deg, transparent 0%, ${props.$vibrantColor} 50%)`};
    }
  }

  &[data-is-slider='true'] {
    &&& {
      border-radius: 0;

      &::after {
        transform: scaleY(1);
        background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.1) 0%,
          rgba(0, 0, 0, 0) 30%
        );
      }
    }
    @media ${mediaMobileOnly} {
      width: 100%;
    }

    ${VideoOrFallbackImageWrapper} {
      height: 100%;
    }

    ${CardImageContent}::before {
      display: none;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      background: ${props =>
        `linear-gradient(
        180deg,
        transparent 60%,
        rgba(0, 0, 0, 0.2) 100%
      ), linear-gradient(180deg, transparent 40%, ${props.$vibrantColor} 90%)`};
    }
  }
`;

const StyledBrandLogo = styled(BrandLogo)`
  display: flex;
  justify-content: center;
  width: auto;
  height: auto;
  text-align: center;
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px32};
  font-weight: ${FONT_WEIGHTS.heavy};
`;

export const Headline = styled(Text)`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.3;
  width: 100%;
  max-width: 100%;
`;

const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 100%;

  ${mobileOnlyLinaria(`
    grid-template-columns: repeat(4, 1fr);
  `)}
`;

const StyledProductImage = styled(ProductImage)`
  height: 100%;
  object-fit: contain;
`;

// Each sponsored placement has different header spacing depending on its size and how squeezed the content is.
// You can customize the spacing by simply specifying the --spacing CSS variable.
export const Header = styled(SponsoredPlacementHeader)`
  z-index: 2;
`;
